import {Card} from "./generic/card";
import {CollapsibleRow, ColumnProps, Row, Table} from "./generic/table";
import {useFetchAndRender} from "./fetchAndRender";
import {Button, ToggleIconButton} from "./generic/button";
import {mdiFileTree, mdiFormatListBulletedSquare, mdiMagnify, mdiToggleSwitch, mdiToggleSwitchOff} from "@mdi/js";
import {MaybeReading, WithAPI} from "../api/temperature";
import {Temperature} from "./generic/temperature";
import {DateTime} from "./generic/date";
import Icon from "@mdi/react";
import {getLocalStorageItem, getLocalStorageJSON, setLocalStorageItem, setLocalStorageJSON} from "../lib/localStorage";
import {useState} from "preact/hooks";
import styled from "styled-components";
import {Badge} from "./generic/badge";
import {Sensor, SensorsResponse} from "../api/sensor";
import {SensorSet} from "../api/set";

/**
 * @TODO
 * - Inline editing of sensor data.
 */

type SensorRowProps = {
    sensor: Sensor,
    class?: string,
    className?: string,
};

function TemperatureReadingCell(props: {reading: MaybeReading, className?: string}) {
    return <td class="text-center" style={{verticalAlign: "top"}}>
        <div className={props.className}>
            <Temperature value={props.reading?.value ?? null} />
            {props.reading ? <><br /><small class="text-muted"><DateTime value={props.reading.date} /></small></> : null}
        </div>
    </td>;
}

function SensorRow({sensor, "class": clz, className}: SensorRowProps) {
    return <Row key={sensor.id} class={clz} className={className} cells={{
        "sensor": <th>
            <div className={!sensor.active ? "text-muted" : ""}>
                <a href={sensor.url} class="sensor-name link-invisible">{sensor.name}</a>
                {sensor.sets?.map(set => <Badge accent="muted" as="a" href={set.url}>{set.name}</Badge>)}
            </div>
        </th>,
        "current": <TemperatureReadingCell reading={sensor.last ?? null} />,
        "dayMax": <TemperatureReadingCell reading={sensor.active ? (sensor.dayMax ?? null) : null} className={!sensor.active ? "text-muted" : ""} />,
        "dayMin": <TemperatureReadingCell reading={sensor.active ? (sensor.dayMin ?? null) : null} className={!sensor.active ? "text-muted" : ""} />,
        "show": <td class="td-shrink"><Button as="a" href={sensor.url} className="btn"><Icon size="1em" path={mdiMagnify} /></Button></td>
    }} />;
}

const StyledSensorRow = styled(SensorRow)`
    &.sensor-indented th {
        padding-left: 2rem;
    }
`;

export function SensorsTable(props: WithAPI & { sets: SensorSet[] }) {
    const LOCAL_STORAGE_SENSOR_LIST_CONFIG = "sensor-list-config";
    const LOCAL_STORAGE_SENSOR_LIST_CONFIG_INCLUDE_INACTIVE = "include-inactive";
    const LOCAL_STORAGE_SENSOR_LIST_CONFIG_EXCLUDE_INACTIVE = "";
    const LOCAL_STORAGE_SENSOR_LIST_GROUPS = "sensor-list-group";
    const LOCAL_STORAGE_SENSOR_LIST_GROUPS_YES = "yes";
    const LOCAL_STORAGE_SENSOR_LIST_GROUPS_NO = "no";
    const LOCAL_STORAGE_GROUP_STATE = "group-state";

    const [groupBySets, _setGroupBySets] = useState<boolean>(getLocalStorageItem(
            LOCAL_STORAGE_SENSOR_LIST_GROUPS,
            LOCAL_STORAGE_SENSOR_LIST_GROUPS_NO
        ) == LOCAL_STORAGE_SENSOR_LIST_GROUPS_YES);

    const setGroupBySets = (newGroupBySets: boolean) => {
        _setGroupBySets(newGroupBySets);
        setLocalStorageItem(
            LOCAL_STORAGE_SENSOR_LIST_GROUPS,
            newGroupBySets
                ? LOCAL_STORAGE_SENSOR_LIST_GROUPS_YES
                : LOCAL_STORAGE_SENSOR_LIST_GROUPS_NO
        );
    }

    const [showOnlyActive, _setShowOnlyActive] = useState<boolean>(getLocalStorageItem(
            LOCAL_STORAGE_SENSOR_LIST_CONFIG,
            LOCAL_STORAGE_SENSOR_LIST_CONFIG_INCLUDE_INACTIVE
        ) === LOCAL_STORAGE_SENSOR_LIST_CONFIG_EXCLUDE_INACTIVE);

    const setShowOnlyActive = (newShowOnlyActive: boolean) => {
        _setShowOnlyActive(newShowOnlyActive);
        setLocalStorageItem(
            LOCAL_STORAGE_SENSOR_LIST_CONFIG,
            newShowOnlyActive
                ? LOCAL_STORAGE_SENSOR_LIST_CONFIG_EXCLUDE_INACTIVE
                : LOCAL_STORAGE_SENSOR_LIST_CONFIG_INCLUDE_INACTIVE
        );
    }

    const [result] = useFetchAndRender((showOnlyActive) => {
        return props.api.sensor.get({
            filter: {
                active: showOnlyActive ? true : undefined
            },
            attributes: ["id", "name", "url", "dayMin", "dayMax", "last", "sets", "active"]
        })
    }, {forceShowLoader: true}, [showOnlyActive]);

    const [filter, setFilter] = useState<string>("");

    const getGroupStates = (): {[key: number]: boolean} => {
        const groupStates = getLocalStorageJSON<{[key: string]: boolean}>(LOCAL_STORAGE_GROUP_STATE, {})
        const out: {[key: number]: boolean} = {};

        Object.keys(groupStates).forEach(key => {
            const match = key.match(/sensor-group-(\d+)/);
            if (match) {
                out[Number.parseInt(match[1])] = !!groupStates[key];
            }
        });

        return out;
    };

    const [groupStates, _setGroupStates] = useState<{[key: number]: boolean}>(getGroupStates());

    const setGroupStates = (newGroupStates: {[key: number]: boolean}) => {
        _setGroupStates(newGroupStates);
        saveGroupStates(newGroupStates);
    }

    const saveGroupStates = (newGroupStates: {[key: number]: boolean}) => {
        const lsGroupStates: {[key: string]: boolean} = {};

        Object.keys(newGroupStates).forEach(key => {
            lsGroupStates[`sensor-group-${key}`] = newGroupStates[Number.parseInt(key)];
        });

        setLocalStorageJSON(LOCAL_STORAGE_GROUP_STATE, lsGroupStates);
    };

    const toggleGroup = (group: number, collapsed: boolean) => {
        const newGroupStates = Object.assign(
            {},
            groupStates,
            {[group]: !collapsed}
        );

        setGroupStates(newGroupStates);
    };

    const renderEmptyRows = () => <tr>
        <td colspan={5} class="text-center">
            <em>
                No sensors found.<br/>
                <small>Sensor is automatically created
                when first reading is received.</small>
            </em>
        </td>
    </tr>;

    const renderGroupedRows = () => {
        const rowsBySet: { [key: number]: Sensor[] } = {};

        const sensorsWithoutSet: Sensor[] = [];

        for (const sensor of (result?.sensors ?? [])) {
            for (const set of (sensor.sets ?? [])) {
                if (!Object.prototype.hasOwnProperty.call(rowsBySet, set.id)) {
                    rowsBySet[set.id] = [];
                }

                rowsBySet[set.id].push(sensor);
            }

            if (!sensor.sets || !sensor.sets.length) {
                sensorsWithoutSet.push(sensor);
            }
        }

        return <>
            {props.sets.map(set => {
                return <CollapsibleRow
                    key={set.id}
                    collapsed={!groupStates[set.id]}
                    title={set.name}
                    colspan={5}
                    onToggle={(collapsed) => toggleGroup(set.id, collapsed)}
                >
                    {(rowsBySet[set.id] ?? [])
                        .filter(s => showSensor(s))
                        .map(sensor => <StyledSensorRow key={sensor.id} className="sensor-indented" sensor={sensor} />)
                    }
                </CollapsibleRow>
            })}
            {sensorsWithoutSet
                .filter(s => showSensor(s))
                .map(sensor => <StyledSensorRow key={sensor.id} sensor={sensor} />)}
        </>;
    };

    const renderFlatRows = () => {
        return (result?.sensors ?? [])
            .filter(s => showSensor(s))
            .map((sensor) => <StyledSensorRow key={sensor.id} sensor={sensor} />);
    }

    const renderRows = () => {
        if (result === undefined || result?.sensors.length === 0) {
            return renderEmptyRows();
        }

        if (groupBySets) {
            return renderGroupedRows();
        }

        return renderFlatRows();
    }

    const showSensor = (sensor: Sensor): boolean =>
        sensor.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0;

    return <Card title="Sensors" actions={[
        <ToggleIconButton
            title="Group sensors by sets"
            initialIcon={mdiFormatListBulletedSquare}
            toggledIcon={mdiFileTree}
            toggled={groupBySets}
            whenToggled={(toggled) => {
                setGroupBySets(toggled);
            }}
            className="btn"
        />,
        <ToggleIconButton
            title={() => showOnlyActive ? "Click to show inactive sensors." : "Click to hide inactive sensors."}
            initialIcon={mdiToggleSwitch}
            toggledIcon={mdiToggleSwitchOff}
            toggled={showOnlyActive}
            whenToggled={(toggled) => {
                setShowOnlyActive(toggled);
            }}
            className="btn"
        />
    ]}>
        <Table columns={[
            {id: "sensor", title: "Sensor", filter: true, class: "text-left"},
            {id: "current", title: "Current"},
            {id: "dayMax", title: "Day max"},
            {id: "dayMin", title: "Day min"},
            {id: "show", class: "td-shrink"}
        ]} onFilter={(column: ColumnProps, value: string) => setFilter(value)}>
            {renderRows()}
        </Table>
    </Card>
}
